/**
 * Шапка и вертикальная раскладка приложения (§3, §4.1).
 *
 * Слева направо: логотип, слот экрана (канал и формат отображения), справа —
 * инструменты экрана (колонки, фильтры, выгрузка), переключатель темы, чип
 * черновика и меню аватара. Док черновика (§6.1) — слот уровня приложения,
 * его наполняет волна черновика через проп `dock`.
 *
 * Поиска в шапке на большом экране нет: он переехал в шапку таблицы, в блок
 * «Бренд» — искать удобнее там, где лежат названия. На телефоне таблицы нет,
 * поэтому поиск остаётся здесь.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Sidebar, navItems } from './Sidebar.js'
import type { ChannelCode, PermissionCode } from '@shared/types.ts'
import { IconCheck, IconClose as IconCloseGlyph, IconDisplay, IconMoon, IconSun, Logo } from '../components/index.js'
import { api } from '../lib/api.js'
import { useApp } from '../store/app-store.js'
import { countOf, forms, texts } from '../texts.js'
import { getPrefs, setPref, subscribePrefs, type ThemePref } from './prefs.js'
import { useFreshBuild } from './useFreshBuild.js'
import { useIsMobile } from './useIsMobile.js'
import './AppShell.css'

/** Метку сборки подставляет vite (define в vite.config.ts). */
const BUILD_STAMP = __BUILD_STAMP__

/**
 * «/» просит поле поиска взять фокус. Поле живёт в разных местах (шапка на
 * телефоне, шапка таблицы на большом экране), поэтому связь через событие:
 * слушает тот, кто сейчас на экране.
 */
export const FOCUS_SEARCH = 'bm:focus-search'

// ───────────────────────────── иконки (§: инлайновые SVG) ────────────────────
// Пока нет components/icons.tsx, нужные три иконки живут здесь.

function IconSearch() {
  return (
    <svg className="icon" width="16" height="16" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.2 10.2 13.5 13.5" />
    </svg>
  )
}

function IconChevronDown() {
  return (
    <svg className="icon" width="16" height="16" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6.5 8 10.5l4-4" />
    </svg>
  )
}

function IconClose() {
  return (
    <svg className="icon" width="16" height="16" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  )
}

// ───────────────────────────── вспомогательное ───────────────────────────────

/** Детерминированный оттенок аватара из гаммы §2.3 (5 оттенков). */
function avatarTone(seed: string): 1 | 2 | 3 | 4 | 5 {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 100000
  return ((hash % 5) + 1) as 1 | 2 | 3 | 4 | 5
}

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second) || '—'
}

function timeOf(iso: string | null): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

/** Esc и клик вне — закрыть, фокус вернуть источнику (§13). */
function useDismiss(open: boolean, close: () => void, anchor: React.RefObject<HTMLElement>) {
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close()
        anchor.current?.focus()
      }
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (box.current?.contains(target)) return
      if (anchor.current?.contains(target)) return
      close()
    }
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open, close, anchor])

  return box
}

/** ↑↓ Home End и первая буква внутри меню (§2.3 Menu). */
function menuKeyNav(event: React.KeyboardEvent<HTMLDivElement>): void {
  // Недоступные пункты остаются в проходе по ↑↓: причина должна читаться (§11.4).
  const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[data-menu-item]')]
  if (items.length === 0) return
  const active = document.activeElement as HTMLElement | null
  const index = active ? items.indexOf(active) : -1

  const step = (delta: number) => {
    event.preventDefault()
    const next = items[(index + delta + items.length) % items.length]
    next?.focus()
  }

  if (event.key === 'ArrowDown') return step(1)
  if (event.key === 'ArrowUp') return step(-1)
  if (event.key === 'Home') { event.preventDefault(); items[0]?.focus(); return }
  if (event.key === 'End') { event.preventDefault(); items[items.length - 1]?.focus(); return }
  if (event.key.length === 1 && /\p{L}/u.test(event.key)) {
    const letter = event.key.toLowerCase()
    const hit = items.find((item, i) => i > index && (item.textContent ?? '').trim().toLowerCase().startsWith(letter))
      ?? items.find((item) => (item.textContent ?? '').trim().toLowerCase().startsWith(letter))
    if (hit) { event.preventDefault(); hit.focus() }
  }
}

function usePrefsState() {
  const [, force] = useState(0)
  useEffect(() => subscribePrefs(() => force((n) => n + 1)), [])
  return getPrefs()
}

// ───────────────────────────── оболочка ──────────────────────────────────────

export interface AppShellProps {
  children: React.ReactNode
  /** Док черновика (§6.1). Пока волна черновика не готова — слот пуст. */
  dock?: React.ReactNode
  /**
   * Плашки под шапкой (§4.1): «данные обновлены» (§11.7) и «возврат к
   * черновику» (§6.6). Закреплены вместе с шапкой, тело под ними сжимается.
   */
  banners?: React.ReactNode
  /** Первая загрузка: пользователь ещё неизвестен, хром рисуется без него (§11.1). */
  loading?: boolean
}

export function AppShell({ children, dock, banners, loading = false }: AppShellProps) {
  const bootstrap = useApp((s) => s.bootstrap)
  const permissions = bootstrap?.user.permissions ?? []
  const can = (code: PermissionCode) => permissions.includes(code)

  const { pathname } = useLocation()
  const isCatalog = pathname.startsWith('/catalog')

  /* Вкладка живёт дольше выкладки: без этой проверки человек работает в
     прошлой сборке и видит починенное как сломанное. */
  const staleBuild = useFreshBuild(BUILD_STAMP)

  const [params] = useSearchParams()
  const rawChannel = params.get('ch')
  const channel: ChannelCode =
    rawChannel === 'retail' || rawChannel === 'b2b'
      ? rawChannel
      : bootstrap?.settings.defaultChannel ?? 'retail'

  const searchRef = useRef<HTMLInputElement>(null)
  const mobile = useIsMobile()
  const undoLast = useApp((s) => s.undoLast)

  /* Cmd+Z отменяет последнюю правку черновика. Раньше отменить можно было
     только весь черновик целиком: одна ошибочная правка после пяти верных
     стоила всех шести. Внутри поля ввода клавиша остаётся браузерной —
     там она отменяет набранный текст. */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'z' && event.key !== 'Z' && event.key !== 'я' && event.key !== 'Я') return
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return
      const target = event.target as HTMLElement | null
      const typing =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      if (typing) return
      event.preventDefault()
      void undoLast()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [undoLast])

  /* «/» ставит фокус в поиск из любого места. Поле поиска живёт то в шапке
     (телефон), то в шапке таблицы (большой экран), поэтому клавиша не держит
     ссылку на него, а кричит в окно — слушает тот, кто сейчас нарисован. */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      const typing =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      if (typing) return
      if (event.key === '/') {
        event.preventDefault()
        if (searchRef.current) {
          searchRef.current.focus()
          searchRef.current.select()
          return
        }
        window.dispatchEvent(new CustomEvent(FOCUS_SEARCH))
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="shell">
      <a className="shell__skip visually-hidden--focusable" href="#main">
        {texts.topbar.skipToContent}
      </a>

      {/* Полоса о новой версии. Перезагружает человек: посреди правки
          перезагрузка стёрла бы набранный текст. */}
      {staleBuild ? (
        <div className="shell__update" role="status">
          <span>{texts.topbar.updateReady}</span>
          <button type="button" className="shell__update-btn" onClick={() => window.location.reload()}>
            {texts.topbar.updateAction}
          </button>
        </div>
      ) : null}

      <Sidebar channel={channel} loading={loading} can={can} pathname={pathname} />

      <div className="shell__main">
      <header className="topbar" role="banner">
        <Link className="topbar__logo" to={`/brands?ch=${channel}`} aria-label={texts.topbar.logoAriaLabel}>
          <Logo height={50} />
        </Link>

        {/* На телефоне таблицы нет, а значит нет и её шапки: поиск остаётся
            в верхней строке. */}
        {mobile ? <SearchField inputRef={searchRef} channel={channel} disabled={loading} /> : null}
        {mobile ? <MobileMenu channel={channel} can={can} loading={loading} /> : null}

        {/* Слева экран кладёт то, чем переключают вид: канал и формат. */}
        <div className="topbar__slot" id="bm-toolbar" />

        <div className="topbar__right">
          {/* Инструменты экрана — у правого края, рядом с аккаунтом: это
              настройки показа, а не содержание. */}
          <div className="topbar__tools" id="bm-toolbar-right" />
          {loading ? (
            <span className="topbar__placeholder" aria-hidden="true" />
          ) : (
            <>
              {/* На телефоне тема и аккаунт живут в бургере: в строке
                  шириной 375 px им места нет, а пользуются ими раз в месяц. */}
              {mobile ? null : <ThemeMenu />}
              <DraftChip channel={channel} />
              {mobile ? null : <UserMenu />}
            </>
          )}
        </div>
      </header>

      {banners ? <div className="shell__banners">{banners}</div> : null}

      <div className="shell__body" id="main" role="main" tabIndex={-1}>
        {children}
      </div>

      {dock ? <div className="shell__dock">{dock}</div> : null}
      </div>

      <ToastHost />
    </div>
  )
}

// ───────────────────────────── поиск ─────────────────────────────────────────

function SearchField({
  inputRef, channel, disabled,
}: {
  inputRef: React.RefObject<HTMLInputElement>
  channel: ChannelCode
  disabled: boolean
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [params, setParams] = useSearchParams()
  const onBrands = pathname === '/brands' || pathname.startsWith('/brands/')
  const urlQuery = onBrands ? params.get('q') ?? '' : ''
  const [value, setValue] = useState(urlQuery)

  useEffect(() => { setValue(urlQuery) }, [urlQuery])

  const push = useCallback((next: string) => {
    const search = new URLSearchParams(params)
    search.set('ch', channel)
    if (next) search.set('q', next)
    else search.delete('q')
    setParams(search, { replace: true })
  }, [params, setParams, channel])

  return (
    <form
      className="topbar__search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault()
        if (onBrands) push(value)
        else navigate(`/brands?ch=${channel}${value ? `&q=${encodeURIComponent(value)}` : ''}`)
      }}
    >
      <span className="topbar__search-icon"><IconSearch /></span>
      <input
        ref={inputRef}
        className="topbar__search-input"
        type="search"
        value={value}
        disabled={disabled}
        placeholder={texts.topbar.searchPlaceholder}
        aria-label={texts.topbar.searchLabel}
        autoComplete="off"
        onChange={(event) => {
          setValue(event.target.value)
          if (onBrands) push(event.target.value)
        }}
      />
    </form>
  )
}

// ───────────────────────────── чип черновика (§6.2) ──────────────────────────

function DraftChip({ channel }: { channel: ChannelCode }) {
  const navigate = useNavigate()
  const changes = useApp((s) => s.draftChanges)
  const brandsById = useApp((s) => s.brandsById)
  const dropChange = useApp((s) => s.dropChange)
  const discardDraft = useApp((s) => s.discardDraft)
  const permissions = useApp((s) => s.bootstrap?.user.permissions) ?? []

  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  const box = useDismiss(open, close, anchor)

  // Наблюдатель черновика не ведёт — ни дока, ни чипа (§11.4).
  const mayEdit = permissions.includes('brands.edit') || permissions.includes('brands.create')

  const groups = useMemo(() => {
    const byBrand = new Map<number, { name: string; count: number; changeIds: number[] }>()
    for (const change of changes) {
      if (change.brandId == null) continue
      const name = brandsById.get(change.brandId)?.name ?? change.entityLabel.split(' · ')[0] ?? '—'
      const row = byBrand.get(change.brandId) ?? { name, count: 0, changeIds: [] }
      row.count += 1
      row.changeIds.push(change.id)
      byBrand.set(change.brandId, row)
    }
    return [...byBrand.entries()].map(([brandId, row]) => ({ brandId, ...row }))
  }, [changes, brandsById])

  if (!mayEdit || changes.length === 0) return null

  const since = timeOf(changes[0]?.createdAt ?? null)
  const shown = groups.slice(0, 3)
  const rest = groups.length - shown.length

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="draft-chip"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={texts.topbar.draftChipLabel(changes.length)}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="draft-chip__pill">
          <span className="draft-chip__dot" aria-hidden="true" />
          <span aria-live="polite">{texts.topbar.draftChip(changes.length)}</span>
        </span>
      </button>

      {open && (
        <div className="draft-pop" ref={box} role="dialog" aria-label={texts.screen.draft.title}>
          <div className="draft-pop__head">
            {texts.draftPopover.heading(changes.length, groups.length, since)}
          </div>

          <div className="draft-pop__list">
            {shown.map((group) => (
              <div className="draft-pop__row" key={group.brandId}>
                <button
                  type="button"
                  className="draft-pop__brand"
                  onClick={() => {
                    close()
                    navigate(`/brands?ch=${channel}&sel=${group.brandId}`)
                  }}
                >
                  {group.name}
                </button>
                <span className="draft-pop__count">{group.count}</span>
                <button
                  type="button"
                  className="draft-pop__drop"
                  aria-label={texts.draftPopover.dropBrand(group.name)}
                  onClick={() => { for (const id of group.changeIds) void dropChange(id) }}
                >
                  <IconClose />
                </button>
              </div>
            ))}
            {rest > 0 && <div className="draft-pop__more">{texts.draftPopover.more(rest)}</div>}
          </div>

          <div className="draft-pop__foot">
            <Link className="draft-pop__link" to="/draft" onClick={close}>
              {texts.draftPopover.openReview}
            </Link>
            <button
              type="button"
              className="draft-pop__link draft-pop__link--danger"
              onClick={() => { close(); void discardDraft() }}
            >
              {texts.draftPopover.discard}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

// ───────────────────────────── меню телефона ────────────────────────────────

/**
 * Бургер в шапке телефона. Панель вкладок внизу экрана занимала 60 px
 * постоянно, а нажимали в ней один раздел из шести. Теперь разделы, тема и
 * выход лежат за одной кнопкой, а освободившееся место отдано списку брендов.
 */
function MobileMenu({ channel, can, loading }: {
  channel: ChannelCode
  can: (permission: PermissionCode) => boolean
  loading: boolean
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const user = useApp((s) => s.bootstrap?.user)
  const stopPolling = useApp((s) => s.stopPolling)
  const prefs = usePrefsState()

  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  const box = useDismiss(open, close, anchor)

  /* Пока меню открыто, страница под ним не прокручивается. */
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [open])

  const items = navItems(channel, can, loading)

  async function logout() {
    close()
    stopPolling()
    try { await api.logout() } catch { /* всё равно уводим на вход */ }
    useApp.setState({ status: 'idle', error: null, bootstrap: null })
    navigate('/login', { replace: true })
  }

  const tone = user ? avatarTone(user.fullName) : 1

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="burger"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={texts.topbar.nav.label}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="burger__bar" aria-hidden="true" />
        <span className="burger__bar" aria-hidden="true" />
        <span className="burger__bar" aria-hidden="true" />
      </button>

      {open ? (
        <>
          <div className="mobile-menu__veil" aria-hidden="true" />
          <div className="mobile-menu" ref={box} role="menu" aria-label={texts.topbar.nav.label}>
            <div className="mobile-menu__head">
              {user ? (
                <>
                  <span className={`avatar avatar--l avatar--a${tone}`} aria-hidden="true">
                    {initials(user.fullName)}
                  </span>
                  <span className="mobile-menu__who">
                    <span className="mobile-menu__name">{user.fullName}</span>
                    <span className="mobile-menu__role">{user.jobTitle ?? user.roleName}</span>
                  </span>
                </>
              ) : <span />}
              <button type="button" className="mobile-menu__close" aria-label={texts.action.close} onClick={close}>
                <IconCloseGlyph size={20} />
              </button>
            </div>

            <nav className="mobile-menu__nav">
              {items.map((item) => {
                const active = item.match
                  ? item.match(pathname)
                  : pathname.startsWith(item.to.split('?')[0] ?? item.to)
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    className={active ? 'mobile-menu__item mobile-menu__item--on' : 'mobile-menu__item'}
                    onClick={close}
                  >
                    <span className="mobile-menu__icon" aria-hidden="true">{item.icon}</span>
                    {item.label}
                  </NavLink>
                )
              })}
            </nav>

            <div className="mobile-menu__section">
              <span className="mobile-menu__label">{texts.topbar.theme.label}</span>
              <div className="mobile-menu__themes">
                {([
                  { value: 'light' as ThemePref, label: texts.topbar.theme.light, icon: <IconSun size={16} /> },
                  { value: 'dark' as ThemePref, label: texts.topbar.theme.dark, icon: <IconMoon size={16} /> },
                  { value: 'system' as ThemePref, label: texts.topbar.theme.system, icon: <IconDisplay size={16} /> },
                ]).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={prefs.theme === option.value}
                    className="mobile-menu__theme"
                    onClick={() => setPref('theme', option.value)}
                  >
                    <span aria-hidden="true">{option.icon}</span>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="mobile-menu__item mobile-menu__item--danger"
              role="menuitem"
              onClick={() => void logout()}
            >
              {texts.topbar.logout}
            </button>

            <span className="mobile-menu__build t-meta">{texts.topbar.build(BUILD_STAMP)}</span>
          </div>
        </>
      ) : null}
    </>
  )
}

// ───────────────────────────── переключатель темы ────────────────────────────

/** Тема, которая реально нарисована: при «как в системе» её выбирает система. */
function useEffectiveTheme(pref: ThemePref): 'light' | 'dark' {
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches,
  )

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches)
    media.addEventListener('change', onChange)
    setSystemDark(media.matches)
    return () => media.removeEventListener('change', onChange)
  }, [])

  if (pref === 'light') return 'light'
  if (pref === 'dark') return 'dark'
  return systemDark ? 'dark' : 'light'
}

function ThemeMenu() {
  const prefs = usePrefsState()
  const effective = useEffectiveTheme(prefs.theme)

  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  const box = useDismiss(open, close, anchor)

  useEffect(() => {
    if (!open) return
    box.current?.querySelector<HTMLElement>('[data-menu-item]')?.focus()
  }, [open, box])

  /* На кнопке написано, что сейчас на экране, а не что выбрано в настройке:
     при «как в системе» человеку важно знать, светлая у него тема или тёмная. */
  const current = effective === 'dark'
    ? { icon: <IconMoon size={16} />, label: texts.topbar.theme.dark }
    : { icon: <IconSun size={16} />, label: texts.topbar.theme.light }

  const options: { value: ThemePref; label: string; icon: JSX.Element }[] = [
    { value: 'light', label: texts.topbar.theme.light, icon: <IconSun size={16} /> },
    { value: 'dark', label: texts.topbar.theme.dark, icon: <IconMoon size={16} /> },
    { value: 'system', label: texts.topbar.theme.system, icon: <IconDisplay size={16} /> },
  ]

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="theme-btn"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`${texts.topbar.theme.label}: ${current.label}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="theme-btn__icon" aria-hidden="true">{current.icon}</span>
        <span className="theme-btn__label">{current.label}</span>
        <IconChevronDown />
      </button>

      {open && (
        <div
          className="theme-menu"
          ref={box}
          role="menu"
          aria-label={texts.topbar.theme.label}
          onKeyDown={menuKeyNav}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="theme-menu__item"
              role="menuitemradio"
              data-menu-item
              aria-checked={prefs.theme === option.value}
              onClick={() => { setPref('theme', option.value); close() }}
            >
              <span className="theme-menu__icon" aria-hidden="true">{option.icon}</span>
              <span className="theme-menu__label">{option.label}</span>
              <span className="theme-menu__check" aria-hidden="true">
                {prefs.theme === option.value ? <IconCheck size={16} /> : null}
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  )
}

// ───────────────────────────── меню аватара (§3) ─────────────────────────────

function UserMenu() {
  const navigate = useNavigate()
  const user = useApp((s) => s.bootstrap?.user)
  const stopPolling = useApp((s) => s.stopPolling)

  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  const box = useDismiss(open, close, anchor)

  useEffect(() => {
    if (!open) return
    const first = box.current?.querySelector<HTMLElement>('[data-menu-item]')
    first?.focus()
  }, [open, box])

  if (!user) return null

  const tone = avatarTone(user.fullName)
  const canUsers = user.permissions.includes('users.manage')

  async function logout() {
    close()
    stopPolling()
    try { await api.logout() } catch { /* всё равно уводим на вход */ }
    useApp.setState({ status: 'idle', error: null, bootstrap: null })
    navigate('/login', { replace: true })
  }

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="user-btn"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={texts.topbar.userMenu}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`avatar avatar--a${tone}`} aria-hidden="true">{initials(user.fullName)}</span>
        <IconChevronDown />
      </button>

      {open && (
        <div className="user-menu" ref={box} role="menu" aria-label={texts.topbar.userMenu} onKeyDown={menuKeyNav}>
          <div className="user-menu__head">
            <span className={`avatar avatar--l avatar--a${tone}`} aria-hidden="true">
              {initials(user.fullName)}
            </span>
            <span className="user-user-menu__head-text">
              <span className="user-menu__name">{user.fullName}</span>
              <span className="user-menu__role">{user.jobTitle ?? user.roleName}</span>
            </span>
          </div>

          <div className="user-menu__sep" />

          {/* В меню остались только разделы и выход. Плотность и тема живут
              в верхней строке: ими пользуются по ходу работы, а не раз в
              полгода. «Правка», «Справка и горячие клавиши» и «Моя зона
              ответственности» убраны — первая ничего не меняла до
              перезагрузки, вторая слала событие, которое никто не слушает,
              третья была заглушкой. */}
          {canUsers && (
            <Link className="user-menu__item" role="menuitem" data-menu-item to="/users" onClick={close}>
              {texts.topbar.usersAndRoles}
            </Link>
          )}

          <div className="user-menu__sep" />

          {/* Версия сборки: по ней видно, что у человека в браузере — свежий
              интерфейс или прошлый, из кэша. */}
          <span className="user-menu__build t-meta">{texts.topbar.build(BUILD_STAMP)}</span>

          <div className="user-menu__sep" />

          <button
            type="button"
            className="user-menu__item user-user-menu__item--danger"
            role="menuitem"
            data-menu-item
            onClick={() => void logout()}
          >
            {texts.topbar.logout}
          </button>
        </div>
      )}
    </>
  )
}

// ───────────────────────────── тосты (§2.3) ──────────────────────────────────
// Временная верстка: заменяется на components/ToastHost, когда он появится.

function ToastHost() {
  const toasts = useApp((s) => s.toasts)
  const dismiss = useApp((s) => s.dismissToast)
  if (toasts.length === 0) return null

  return (
    <div className="toast-host">
      {toasts.slice(-3).map((toast) => (
        <div
          key={toast.id}
          className={`toast toast--${toast.kind}`}
          role={toast.kind === 'error' ? 'alert' : 'status'}
        >
          <span>{toast.text}</span>
          <button
            type="button"
            className="toast__close"
            aria-label={texts.action.close}
            onClick={() => dismiss(toast.id)}
          >
            <IconClose />
          </button>
        </div>
      ))}
    </div>
  )
}

/** Экспортируется для дока черновика: «Черновик · 14 правок в 6 брендах». */
export function draftSummary(changes: number, brands: number): string {
  return `${countOf(changes, forms.change)} в ${countOf(brands, forms.brandIn)}`
}
